import { useState, useCallback } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { updateDisplayName } from '../../../../utils/services/complexDocumentsAPI';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../../context/complexDocumentContext';
import { DESIGN_AREA_SETTINGS_TAB } from '../../../SidebarMenu/translationValues';
import { extractUnsupportedCharacters } from '../../../../utils/helper';

const NAMESPACES = ['DESIGN_AREA_SETTINGS_TAB'];

const DEFAULT_VALUE = [
  {
    contentType: 'PARAGRAPH',
    content: ''
  },
  {
    contentType: "LINE_BREAK",
    content: "\n"
  }
];

export const useDisplayNameSettings = () => {
  const { t } = useTranslation(NAMESPACES);
  const documentIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [documentId, store] = useDocumentContext(documentIdSelector, 'useDisplayNameSettings');
  
  const displayNameSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DISPLAY_NAME], []);
  const [displayNameDTO] = useDocumentContext(displayNameSelector, 'useDisplayNameSettings');
  
  const [isLoading, setIsLoading] = useState(false);
  const [displayNameModalVisible, setDisplayNameModalVisible] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  const openDisplayNameModal = useCallback(() => {
    setEditorKey(prev => prev + 1);
    setDisplayNameModalVisible(true);
  }, []);

  const closeDisplayNameModal = useCallback(() => {
    setEditorKey(prev => prev + 1);
    setDisplayNameModalVisible(false);
  }, []);

  const saveDisplayName = useCallback(
    async data => {
      try {
        setIsLoading(true);
        const payload = {};
        payload.documentId = documentId;
        payload.displayNameBlockDTO = {
          blockType: 'text',
          contents: data.contents,
          variableContract: data.variableContract,
          orderNumber: 1,
          isDeleted: false,
          isEditable: false,
          isStandalone: false,
          blockDTOList: [],
          isTranslated: false,
          style: {
            accessible: true,
          },
        };
        const response = await updateDisplayName(documentId, payload);
        if (response?.statusCode === 2001) {
          store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.DISPLAY_NAME]: payload.displayNameBlockDTO });
          setDisplayNameModalVisible(false);
        } else if (response?.statusCode === 2000) {
          const error = response?.errors?.[0];
          if (error?.code === 5601) {
            const unsupportedChars = extractUnsupportedCharacters(error.description);
            message.error(t(
              DESIGN_AREA_SETTINGS_TAB.DISPLAY_NAME_UNSUPPORTED_CHARS.key,
              DESIGN_AREA_SETTINGS_TAB.DISPLAY_NAME_UNSUPPORTED_CHARS.value,
              { char: unsupportedChars }
            ));
          } else {
            message.error(
              t(
                DESIGN_AREA_SETTINGS_TAB.DISPLAY_NAME_UPDATE_FAIL_ERROR_MESSAGE.key,
                DESIGN_AREA_SETTINGS_TAB.DISPLAY_NAME_UPDATE_FAIL_ERROR_MESSAGE.value,
              ),
            );
          }
        } else {
          message.error(
            t(
              DESIGN_AREA_SETTINGS_TAB.DISPLAY_NAME_UPDATE_FAIL_ERROR_MESSAGE.key,
              DESIGN_AREA_SETTINGS_TAB.DISPLAY_NAME_UPDATE_FAIL_ERROR_MESSAGE.value,
            ),
          );
        }
      } catch (e) {
        console.log(e);
      } finally {
        setIsLoading(false);
      }
    },
    [documentId, store, t],
  );

  return {
    displayNameDTO: displayNameDTO || { contents: DEFAULT_VALUE },
    displayNameModalVisible,
    openDisplayNameModal,
    closeDisplayNameModal,
    saveDisplayName,
    isLoading,
    editorKey,
    DEFAULT_VALUE,
    documentId
  };
};


